import { memo, useMemo, useCallback } from 'react';
import { AnimatedNumber } from '../common/AnimatedNumber';
import { InfoButton } from '../common/Tooltip';
import { formatCurrencyCompact, formatNumber, formatPrice, pnlColorClass } from '../../lib/formatters';
import { Layers, TrendingUp, TrendingDown } from 'lucide-react';
import type { PortfolioPnLResult } from '../../types';
import { useSimulatorStore } from '../../stores/useSimulatorStore';

interface PortfolioPnLSummaryProps {
  portfolio: PortfolioPnLResult;
  demandMode: 'ceiling' | 'split';
  onToggleDemandMode: () => void;
}

export const PortfolioPnLSummary = memo(function PortfolioPnLSummary({ portfolio, demandMode, onToggleDemandMode }: PortfolioPnLSummaryProps) {
  const packages = useSimulatorStore(s => s.packages);
  const cannibalizationRate = useSimulatorStore(s => s.assumptions.cannibalizationRate ?? 0);
  const setCannibalization = useCallback((rate: number) => useSimulatorStore.getState().setAssumption('cannibalizationRate', rate), []);
  const isCeiling = demandMode === 'ceiling';

  const pnl = isCeiling ? portfolio.portfolioPnLCeiling : portfolio.portfolioPnLSplit;
  const annualPnl = isCeiling ? portfolio.annualPnLCeiling : portfolio.annualPnLSplit;
  const totalUnits = isCeiling ? portfolio.totalS2UnitsCeiling : portfolio.totalS2UnitsSplit;
  const totalRevenue = isCeiling ? portfolio.totalNetRevenueCeiling : portfolio.totalNetRevenueSplit;
  const totalContribution = isCeiling ? portfolio.totalContributionCeiling : portfolio.totalContributionSplit;
  const isProfitable = pnl >= 0;

  const waterfallItems = useMemo(() => portfolio.packages.map((pkg, i) => ({
    label: packages[i].name,
    value: isCeiling ? pkg.contributionMarginCeiling : pkg.contributionMarginSplit,
    price: packages[i].price,
  })), [portfolio.packages, packages, isCeiling]);

  const maxAbsValue = useMemo(() => Math.max(
    ...waterfallItems.map(w => Math.abs(w.value)),
    Math.abs(portfolio.fixedCosts),
    Math.abs(pnl),
    1
  ), [waterfallItems, portfolio.fixedCosts, pnl]);

  return (
    <div className={`card mb-6 ${isProfitable ? 'border-metric-positive/30' : 'border-metric-negative/30'}`} style={{ borderLeft: `3px solid ${isProfitable ? 'var(--color-metric-positive)' : 'var(--color-metric-negative)'}` }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Layers size={16} className={isProfitable ? 'text-metric-positive' : 'text-metric-negative'} />
          <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Portfolio P&L</h3>
          <InfoButton helpKey="portfolioPnL" size={13} />
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onToggleDemandMode}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-micro font-medium uppercase tracking-wider transition-colors border border-border hover:border-chart-teal/50"
            style={{ backgroundColor: 'var(--color-surface)' }}
          >
            <span className={isCeiling ? 'text-chart-teal' : 'text-muted'}>Ceiling</span>
            <span className="text-muted">/</span>
            <span className={!isCeiling ? 'text-chart-amber' : 'text-muted'}>Split</span>
          </button>
          <InfoButton helpKey="ceilingVsSplit" size={11} />
        </div>
      </div>

      <div className="text-xs text-muted mb-2 px-1">
        {isCeiling
          ? 'Ceiling: each package captures its full conjoint demand independently (optimistic, no cannibalization).'
          : `Split: demand shared proportionally across packages. Cannibalization: ${(cannibalizationRate * 100).toFixed(0)}% of higher-tier demand trades down.`}
      </div>

      {!isCeiling && (
        <div className="flex items-center gap-3 mb-4 px-1">
          <span className="text-xs text-muted shrink-0 flex items-center gap-1">Cannibalization: <InfoButton helpKey="cannibalizationRate" /></span>
          <input
            type="range"
            min={0} max={50} step={5}
            value={cannibalizationRate * 100}
            onChange={e => setCannibalization(parseFloat(e.target.value) / 100)}
            className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
            style={{ accentColor: '#F59E0B' }}
          />
          <span className="text-xs font-mono text-chart-amber font-semibold w-8">{(cannibalizationRate * 100).toFixed(0)}%</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1 flex items-center gap-1">Monthly P&L <InfoButton helpKey="monthlyPnL" size={10} /></div>
          <AnimatedNumber
            value={pnl}
            formatter={v => formatCurrencyCompact(v)}
            className={`text-2xl font-bold font-mono ${pnlColorClass(pnl)}`}
          />
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1">Annual P&L</div>
          <AnimatedNumber
            value={annualPnl}
            formatter={v => formatCurrencyCompact(v)}
            className={`text-xl font-bold font-mono ${pnlColorClass(annualPnl)}`}
          />
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1 flex items-center gap-1">Total S2 Units <InfoButton helpKey="s2Units" size={10} /></div>
          <AnimatedNumber
            value={totalUnits}
            formatter={v => formatNumber(v)}
            className="text-xl font-bold font-mono text-primary"
          />
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1 flex items-center gap-1">Portfolio Breakeven <InfoButton helpKey="breakevenUnits" size={10} /></div>
          <span className="text-xl font-bold text-primary font-mono">
            {portfolio.portfolioBreakevenUnits >= 999999 ? 'N/A' : formatNumber(portfolio.portfolioBreakevenUnits)}
          </span>
          <div className="text-xs text-muted">units across all packages</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1 flex items-center gap-1">Contribution Margin % <InfoButton helpKey="contributionMarginPct" /></div>
          <span className={`text-lg font-bold font-mono ${pnlColorClass(portfolio.contributionMarginPctSplit)}`}>
            {(portfolio.contributionMarginPctSplit * 100).toFixed(1)}%
          </span>
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1 flex items-center gap-1">Avg Selling Price <InfoButton helpKey="wasp" /></div>
          <span className="text-lg font-bold text-primary font-mono">
            {formatPrice(Math.round(portfolio.weightedAvgSellingPrice))}
          </span>
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1 flex items-center gap-1">S1 Investment ROI <InfoButton helpKey="s1ROI" /></div>
          <span className={`text-lg font-bold font-mono ${portfolio.s1ROI >= 0 ? 'text-metric-positive' : 'text-metric-negative'}`}>
            {(portfolio.s1ROI * 100).toFixed(0)}%
          </span>
        </div>
        <div>
          <div className="text-xs text-muted uppercase tracking-wider mb-1 flex items-center gap-1">Months to Breakeven <InfoButton helpKey="monthsToBreakeven" /></div>
          <span className={`text-lg font-bold font-mono ${portfolio.monthsToBreakeven === 0 ? 'text-metric-positive' : portfolio.monthsToBreakeven >= 999 ? 'text-metric-negative' : 'text-primary'}`}>
            {portfolio.monthsToBreakeven === 0 ? 'Now' : portfolio.monthsToBreakeven >= 999 ? 'N/A' : `${portfolio.monthsToBreakeven} mo`}
          </span>
        </div>
      </div>

      {/* Waterfall visualization */}
      <div className="border-t border-border pt-4">
        <h4 className="text-xs text-muted uppercase tracking-wider mb-3">Contribution Waterfall</h4>
        <div className="space-y-2">
          {waterfallItems.map((item, i) => {
            const barWidth = Math.max(Math.abs(item.value) / maxAbsValue * 100, 2);
            const isPositive = item.value >= 0;
            return (
              <div key={i} className="flex items-center gap-3">
                <div className="w-24 shrink-0 text-right">
                  <div className="text-xs text-secondary font-medium">{item.label}</div>
                  <div className="text-xs text-muted">{formatPrice(item.price)}</div>
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <div className="flex-1 h-6 rounded overflow-hidden" style={{ backgroundColor: 'var(--color-surface)' }}>
                    <div
                      className={`h-full rounded transition-all duration-500 flex items-center px-2 ${isPositive ? 'bg-metric-positive/20' : 'bg-metric-negative/20'}`}
                      style={{ width: `${barWidth}%` }}
                    >
                      {barWidth > 15 && (
                        <span className={`text-xs font-mono font-medium ${pnlColorClass(item.value)}`}>
                          {formatCurrencyCompact(item.value)}
                        </span>
                      )}
                    </div>
                  </div>
                  {barWidth <= 15 && (
                    <span className={`text-xs font-mono font-medium shrink-0 ${pnlColorClass(item.value)}`}>
                      {formatCurrencyCompact(item.value)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {/* Fixed costs bar */}
          <div className="flex items-center gap-3">
            <div className="w-24 shrink-0 text-right">
              <div className="text-xs text-secondary font-medium">Fixed Costs</div>
              <div className="text-xs text-muted">monthly</div>
            </div>
            <div className="flex-1 flex items-center gap-2">
              <div className="flex-1 h-6 rounded overflow-hidden" style={{ backgroundColor: 'var(--color-surface)' }}>
                <div
                  className="h-full rounded transition-all duration-500 flex items-center px-2 bg-metric-negative/20"
                  style={{ width: `${Math.max(portfolio.fixedCosts / maxAbsValue * 100, 2)}%` }}
                >
                  <span className="text-xs font-mono font-medium text-metric-negative">
                    {formatCurrencyCompact(-portfolio.fixedCosts)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Net P&L bar */}
          <div className="flex items-center gap-3 pt-2 border-t border-border/50">
            <div className="w-24 shrink-0 text-right">
              <div className="text-xs font-semibold text-primary">Net P&L</div>
            </div>
            <div className="flex-1 flex items-center gap-2">
              <div className="flex-1 h-7 rounded overflow-hidden" style={{ backgroundColor: 'var(--color-surface)' }}>
                <div
                  className={`h-full rounded transition-all duration-500 flex items-center px-2 ${isProfitable ? 'bg-metric-positive/25' : 'bg-metric-negative/25'}`}
                  style={{ width: `${Math.max(Math.abs(pnl) / maxAbsValue * 100, 2)}%` }}
                >
                  <span className={`text-xs font-mono font-bold ${pnlColorClass(pnl)}`}>
                    {formatCurrencyCompact(pnl)}
                  </span>
                </div>
              </div>
              {isProfitable
                ? <TrendingUp size={14} className="text-metric-positive shrink-0" />
                : <TrendingDown size={14} className="text-metric-negative shrink-0" />
              }
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-border">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">Total Net Revenue</span>
          <AnimatedNumber value={totalRevenue} formatter={v => formatCurrencyCompact(v)} className="font-mono text-secondary" />
        </div>
        <div className="flex items-center justify-between text-xs mt-1">
          <span className="text-muted">Total Contribution (before fixed)</span>
          <AnimatedNumber value={totalContribution} formatter={v => formatCurrencyCompact(v)} className={`font-mono ${pnlColorClass(totalContribution)}`} />
        </div>
        <div className="flex items-center justify-between text-xs mt-1">
          <span className="text-muted">Fixed Costs</span>
          <span className="font-mono text-metric-negative">{formatCurrencyCompact(-portfolio.fixedCosts)}</span>
        </div>
      </div>

      <p className="text-sm text-muted mt-3 pt-2 border-t border-border leading-relaxed">
        Fixed costs ({formatCurrencyCompact(portfolio.fixedCosts)}/mo) are applied once across the entire portfolio, not per-package. Each package shows its contribution margin (net revenue minus variable costs). Portfolio P&L = sum of contributions - fixed costs.
      </p>
    </div>
  );
});
